import Link from 'next/link';
import { getImageUrl, hasCurrentBlockOutDates, hasFutureBlockOutDates } from '../../utils/utils';
import { Item } from '../../types';
import { usePromotionalPricing, PromotionalPricingForBundle, PromotionalPricingForProduct } from '../../contexts/AppContext';
import { BundlePrice, ProductPrice } from './Price';

import tw from '../../../tailwind.config';

const getCollectionUrl = (category: string) => {
  switch (category) {
    case 'Vest':
    case 'Cummerbund':
      return 'vests-and-cummerbunds';
    case 'Shirt':
      return 'shirts';
    case 'Tie':
      return 'ties';
    case 'Suspenders':
    case 'Belt':
    case 'Lapel Pin':
    case 'Tie Bar':
    case 'Cufflinks':
    case 'Pocket Square':
      return 'accessories';
    case 'Shoe':
    case 'Socks':
      return 'shoes-and-socks';
    default:
      return null;
  }
};

const getItemUrl = (item: Item) => {
  if (item.category !== 'preconfigured') {
    return `/collection/${getCollectionUrl(item.category!)}/${item.url_slug}`;
  }

  return `/collection/tuxedos-and-suits/${item.url_slug}`;
};

type PriceLineProps = {
  item: Item;
};

const PriceLine = ({ item }: PriceLineProps) => {
  const promotionalPricing = usePromotionalPricing(item);

  if (item.category === 'preconfigured') {
    return <BundlePrice bundle={item} promotionalPricing={promotionalPricing as PromotionalPricingForBundle | null} />;
  }

  return <ProductPrice product={item} promotionalPricing={promotionalPricing as PromotionalPricingForProduct | null} />;
};

interface Props {
  product: Item;
  isBundle: boolean;
}

function CollectionListItem({ product, isBundle }: Props) {
  const label = 'PDP_Hero';

  const imageUrl = getImageUrl(product, label);

  const generalBlockoutDates = product.blockout_dates?.filter((date) => date.availability === 'general');
  const hasLimitedAvailability = hasCurrentBlockOutDates(generalBlockoutDates ?? []) || hasFutureBlockOutDates(generalBlockoutDates ?? []);

  const comingSoonDates = product.blockout_dates?.filter((date) => date.availability === 'coming-soon');

  const hasComingSoonAvailability = hasCurrentBlockOutDates(comingSoonDates ?? []);

  const imgSrcSet = (size: number) =>
    `${encodeURI(imageUrl!)}${
      isBundle ? `?auto=format&fit=crop&w=${size}&ar=5:7&cs=origin&dpr=1` : `?auto=format&fit=crop&w=${size}&ar=1&cs=origin&dpr=1`
    } 1x, ${encodeURI(imageUrl!)}${
      isBundle ? `?auto=format&fit=crop&w=${size}&ar=5:7&dpr=2&q=30&cs=origin` : `?auto=format&fit=crop&w=${size}&ar=1&dpr=2&q=30&cs=origin`
    } 2x`;

  return (
    <div
      className={`
        col-span-6
        ${isBundle ? 'sm:col-span-6' : 'sm:col-span-4'}
        ${isBundle ? 'md:col-span-4' : 'md:col-span-3'}
      `}
    >
      <Link
        href="/collection/[category]/[slug]"
        as={getItemUrl(product)}
        className="
        group
        relative
        z-10
        block
        transition
        hover:z-40
        hover:no-underline
        "
      >
        <div className="relative mb-8 bg-white">
          <div className={` ${isBundle ? 'aspect-ratio-5by7' : 'aspect-ratio-1by1'} `}>
            <picture>
              <source media={`(min-width: ${tw.theme.screens.xl})`} srcSet={imgSrcSet(480)} width={480} />
              <source media={`(min-width: ${tw.theme.screens.lg})`} srcSet={imgSrcSet(308)} width={308} />
              <source media={`(min-width: ${tw.theme.screens.md})`} srcSet={imgSrcSet(288)} width={288} />
              <source media={`(min-width: ${tw.theme.screens.sm})`} srcSet={imgSrcSet(234)} width={234} />
              <source media={`(min-width: ${tw.theme.screens.xs})`} srcSet={imgSrcSet(187)} width={187} />
              <source srcSet={imgSrcSet(263)} width={263} />
              <img
                src={`${imageUrl}${
                  isBundle ? '?auto=format&fit=crop&w=263&ar=5:7&cs=origin' : '?auto=format&fit=crop&w=263&ar=1&cs=origin'
                }`}
                alt={product.display_name}
                loading="lazy"
                className="
                  block
                  w-full
                  transition
                  duration-200
                  ease-in-out
                  group-hover:opacity-60
                  group-hover:shadow-4
                "
              />
            </picture>
          </div>

          <div
            className="
              absolute
              top-[50%]
              left-[50%]
              translate-x-[-50%]
              translate-y-[100%]
              transform
              whitespace-nowrap
              bg-white
              p-16
              text-black
              opacity-0
              shadow-3
              duration-200
              ease-in-out
              group-hover:translate-y-[-50%]
              group-hover:opacity-100
            "
            style={{ willChange: 'transform, opacity' }}
          >
            <h4 className="text-h6">View Details</h4>
          </div>

          {hasLimitedAvailability && !hasComingSoonAvailability && (
            <div className="absolute bottom-0 left-0 m-8 bg-black py-4 px-8 lg:m-16">
              <p className="text-sm leading-tight text-white">Limited Availability</p>
            </div>
          )}

          {hasComingSoonAvailability && (
            <div className="absolute bottom-0 left-0 m-8 bg-black py-4 px-8 lg:m-16">
              <p className="text-sm leading-tight text-white">Coming Soon</p>
            </div>
          )}
        </div>
        <h2 className="text-h5 mb-4 group-hover:text-gold-darker">{product.display_name}</h2>
        <div>
          <p
            className={`mr-8 inline ${Number(product.cost!) < 100 && product.category === 'preconfigured' ? 'text-danger' : 'text-muted'}`}
          >
            <PriceLine item={product} />
          </p>
        </div>
      </Link>
    </div>
  );
}

export default CollectionListItem;
